import { useMemo, useState, type FormEvent } from 'react';
import { Badge, Card, Progress } from '../../components/ui/Primitives';
import { Dialog } from '../../components/ui/Dialog';
import { useApp } from '../../lib/app/AppContext';

export function ProjectsPage() {
  const { projects, addProject, deleteProject } = useApp();
  const [query, setQuery] = useState('');
  const [team, setTeam] = useState('All teams');
  const [status, setStatus] = useState('All statuses');
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () => projects.filter((project) =>
      project.name.toLowerCase().includes(query.toLowerCase()) &&
      (team === 'All teams' || project.owner === team) &&
      (status === 'All statuses' || project.status === status)
    ),
    [projects, query, team, status],
  );
  const selected = projects.find((project) => project.id === selectedId) ?? null;
  const teams = Array.from(new Set(projects.map((project) => project.owner)));

  function createProject(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const rawName = data.get('name');
    const rawOwner = data.get('owner');
    const rawStatus = data.get('status');
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    const owner = typeof rawOwner === 'string' ? rawOwner.trim() : '';
    const projectStatus = (typeof rawStatus === 'string' ? rawStatus : 'Staging') as 'Production' | 'Staging' | 'Review';
    if (!name || !owner) return;
    addProject({ name, owner, status: projectStatus, health: 100 });
    event.currentTarget.reset();
    setCreating(false);
  }

  return (
    <div className="page-stack">
      <div className="hero-row"><div><span className="page-kicker">Portfolio</span><h1>Projects & environments</h1><p>Track delivery health, release state, and environment readiness across the product portfolio.</p></div><button className="btn primary" onClick={() => setCreating(true)}>New project</button></div>
      <div className="toolbar">
        <div className="search-box">⌕ <input placeholder="Search projects…" value={query} onChange={(e) => setQuery(e.target.value)} /></div>
        <select aria-label="Filter by team" value={team} onChange={(e)=>setTeam(e.target.value)}><option>All teams</option>{teams.map((item)=><option key={item}>{item}</option>)}</select>
        <select aria-label="Filter by status" value={status} onChange={(e)=>setStatus(e.target.value)}><option>All statuses</option><option>Production</option><option>Staging</option><option>Review</option></select>
        <button className="btn secondary" onClick={() => { setQuery(''); setTeam('All teams'); setStatus('All statuses'); }}>Clear filters</button>
      </div>
      {creating ? (
        <Card>
          <form className="inline-create-form" onSubmit={createProject}>
            <label>Name<input name="name" placeholder="Project name" autoFocus /></label>
            <label>Team<input name="owner" placeholder="Team" /></label>
            <label>Status<select name="status" defaultValue="Staging"><option>Staging</option><option>Review</option><option>Production</option></select></label>
            <div><button className="btn primary" type="submit">Create</button><button className="btn ghost" type="button" onClick={() => setCreating(false)}>Cancel</button></div>
          </form>
        </Card>
      ) : null}
      <div className="project-cards">
        {filtered.map((p, idx) => (
          <Card className="project-card" key={p.id}>
            <div className="project-card-top"><div className="project-icon">{String(idx + 1).padStart(2,'0')}</div><Badge tone={p.status==='Production'?'positive':p.status==='Staging'?'info':'warning'}>{p.status}</Badge></div>
            <h3>{p.name}</h3><p>{p.owner} team · {p.deploys} deployments</p>
            <div className="project-health"><span>Health score</span><b>{p.health}%</b></div><Progress value={p.health} />
            <div className="project-footer"><span>Updated {p.updated}</span><div><button className="icon-btn" onClick={() => setSelectedId(p.id)} title="Open project details">→</button><button className="icon-btn danger-icon" onClick={() => deleteProject(p.id)} title="Delete project">×</button></div></div>
          </Card>
        ))}
      </div>
      {!filtered.length ? <div className="empty-state"><strong>No matching projects</strong><span>Change or clear the current filters.</span></div> : null}
      <Dialog open={Boolean(selected)} title={selected?.name ?? 'Project details'} onClose={() => setSelectedId(null)}>
        {selected ? <div className="detail-grid"><span>Team <b>{selected.owner}</b></span><span>Status <b>{selected.status}</b></span><span>Health <b>{selected.health}%</b></span><span>Deployments <b>{selected.deploys}</b></span><span>Last update <b>{selected.updated}</b></span></div> : null}
      </Dialog>
    </div>
  );
}
